// pages/apps/video/ProjectDashboard.tsx - VERSÃO LIMPA SEM WARNINGS
import FeatherIcon from 'feather-icons-react';
import React, { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Row,
  Spinner
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import MapComponent from 'components/common/MapComponent';
import ProjectOverlaySelector from 'components/video/ProjectOverlaySelector';
import SignalOverlayViewModal from 'components/video/SignalOverlayViewModal';

import { useOverlaySigns } from 'hooks/useOverlaySigns';

interface DashboardStats {
  totalSigns: number;
  byService: Record<string, number>;
  byType: Record<string, number>;
  byFilm: Record<string, number>;
  withSupport: number;
  withoutSupport: number;
  withGPSAdjustment: number;
  projectsCount: number;
}

const ProjectDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>([]);
  const [showSignImages, setShowSignImages] = useState(true);

  // Modal de visualização
  const [showOverlayModal, setShowOverlayModal] = useState(false);
  const [selectedOverlaySign, setSelectedOverlaySign] = useState<any>(null);

  // Hook de overlay signs
  const {
    overlaySigns,
    loading: overlayLoading,
    error: overlayError
  } = useOverlaySigns({
    currentProjectId: 0,
    selectedProjectIds,
    enabled: selectedProjectIds.length > 0
  });

  // Calcular estatísticas
  const stats: DashboardStats = useMemo(() => {
    const byService: Record<string, number> = {
      Implantação: 0,
      Remoção: 0,
      Limpeza: 0,
      Manter: 0
    };

    const byType: Record<string, number> = {
      Regulamentação: 0,
      Advertência: 0,
      Indicativa: 0,
      Auxiliares: 0
    };

    const byFilm: Record<string, number> = {
      'Tipo I': 0,
      'Tipo III': 0
    };

    let withSupport = 0;
    let withoutSupport = 0;

    overlaySigns.forEach(sign => {
      const serviceName =
        sign.service_id === 1
          ? 'Implantação'
          : sign.service_id === 2
          ? 'Remoção'
          : sign.service_id === 3
          ? 'Limpeza'
          : sign.service_id === 4
          ? 'Manter'
          : 'Outro';
      byService[serviceName] = (byService[serviceName] || 0) + 1;

      const type = sign.signal_device?.type;
      if (type === 'regulamentacao') byType['Regulamentação']++;
      else if (type === 'advertencia') byType['Advertência']++;
      else if (type === 'indicativa') byType['Indicativa']++;
      else if (type === 'auxiliares') byType['Auxiliares']++;

      const filmName = sign.signal_film_id === 1 ? 'Tipo I' : 'Tipo III';
      byFilm[filmName]++;

      if (sign.signal_support) withSupport++;
      else withoutSupport++;
    });

    return {
      totalSigns: overlaySigns.length,
      byService,
      byType,
      byFilm,
      withSupport,
      withoutSupport,
      withGPSAdjustment: 0,
      projectsCount: selectedProjectIds.length
    };
  }, [overlaySigns, selectedProjectIds]);

  // Criar markers para o mapa
  const mapMarkers = useMemo(() => {
    return overlaySigns
      .filter(sign => {
        const lat = sign.adjusted_lat || sign.lat;
        const lng = sign.adjusted_lng || sign.long;
        return lat && lng;
      })
      .map(sign => {
        const lat = sign.adjusted_lat || sign.lat;
        const lng = sign.adjusted_lng || sign.long;

        let color = '#6c757d';
        if (sign.signal_device?.type === 'regulamentacao') color = '#dc3545';
        else if (sign.signal_device?.type === 'advertencia') color = '#ffc107';
        else if (sign.signal_device?.type === 'indicativa') color = '#0d6efd';

        return {
          id: `dashboard_${sign.projectId}_${sign.id}`,
          lat,
          lng,
          title: `[${sign.projectName}] ${
            sign.signal_device?.name || 'Sinalização'
          } - ${sign.signal_device?.code || 'N/A'}`,
          color,
          icon: '◉',
          type: 'sign' as const,
          symbolName: sign.signal_device?.symbol_name || '',
          isOverlay: false,
          projectName: sign.projectName
        };
      });
  }, [overlaySigns]);

  // Handler de clique no marker
  const handleMarkerClick = (markerId: string) => {
    if (!markerId.startsWith('dashboard_')) return;

    const parts = markerId.replace('dashboard_', '').split('_');
    if (parts.length >= 2) {
      const projectId = parseInt(parts[0]);
      const signId = parseInt(parts[1]);

      const sign = overlaySigns.find(
        s => s.projectId === projectId && s.id === signId
      );

      if (sign) {
        setSelectedOverlaySign(sign);
        setShowOverlayModal(true);
      }
    }
  };

  // Calcular centro do mapa
  const mapCenter = useMemo(() => {
    if (overlaySigns.length === 0) {
      return { lat: -27.5969, lng: -48.5495 };
    }

    const validSigns = overlaySigns.filter(s => {
      const lat = s.adjusted_lat || s.lat;
      const lng = s.adjusted_lng || s.long;
      return lat && lng;
    });

    if (validSigns.length === 0) {
      return { lat: -27.5969, lng: -48.5495 };
    }

    const avgLat =
      validSigns.reduce((sum, s) => sum + (s.adjusted_lat || s.lat), 0) /
      validSigns.length;
    const avgLng =
      validSigns.reduce((sum, s) => sum + (s.adjusted_lng || s.long), 0) /
      validSigns.length;

    return { lat: avgLat, lng: avgLng };
  }, [overlaySigns]);

  return (
    <Container fluid>
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-0">
                <FeatherIcon icon="bar-chart-2" size={28} className="me-2" />
                Dashboard de Sinalizações
              </h2>
              <small className="text-muted">
                Análise consolidada de múltiplos projetos
              </small>
            </div>
            <Button
              variant="outline-secondary"
              onClick={() => navigate('/app/video/projects')}
            >
              <FeatherIcon icon="arrow-left" size={16} className="me-2" />
              Voltar
            </Button>
          </div>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-1">
                    <FeatherIcon icon="folder" size={18} className="me-2" />
                    Selecione os Projetos para Análise
                  </h5>
                  <small className="text-muted">
                    Escolha um ou mais projetos para visualizar estatísticas
                    consolidadas
                  </small>
                </div>
                <ProjectOverlaySelector
                  currentProjectId={0}
                  selectedProjectIds={selectedProjectIds}
                  onProjectsChange={setSelectedProjectIds}
                  proximityRadius={0}
                  onProximityRadiusChange={() => undefined}
                  showProximityRadius={false}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {selectedProjectIds.length === 0 && (
        <Row>
          <Col>
            <Alert variant="info">
              <FeatherIcon icon="info" size={16} className="me-2" />
              Selecione ao menos um projeto para visualizar o dashboard
            </Alert>
          </Col>
        </Row>
      )}

      {overlayLoading && selectedProjectIds.length > 0 && (
        <Row>
          <Col className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">Carregando dados dos projetos...</p>
          </Col>
        </Row>
      )}

      {overlayError && (
        <Row>
          <Col>
            <Alert variant="danger">
              <FeatherIcon icon="alert-circle" size={16} className="me-2" />
              {overlayError}
            </Alert>
          </Col>
        </Row>
      )}

      {selectedProjectIds.length > 0 &&
        !overlayLoading &&
        overlaySigns.length > 0 && (
          <>
            <Row className="mb-3">
              <Col lg={4} md={6} className="mb-3">
                <Card className="h-100 border-primary">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-muted mb-1">Total de Placas</h6>
                        <h2 className="mb-0 text-primary">
                          {stats.totalSigns}
                        </h2>
                      </div>
                      <div
                        className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: '50px', height: '50px' }}
                      >
                        <FeatherIcon
                          icon="navigation"
                          size={24}
                          color="#0d6efd"
                        />
                      </div>
                    </div>
                    <small className="text-muted">
                      Em {stats.projectsCount} projeto(s)
                    </small>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={4} md={6} className="mb-3">
                <Card className="h-100 border-success">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-muted mb-1">Com Suporte</h6>
                        <h2 className="mb-0 text-success">
                          {stats.withSupport}
                        </h2>
                      </div>
                      <div
                        className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: '50px', height: '50px' }}
                      >
                        <FeatherIcon
                          icon="check-circle"
                          size={24}
                          color="#198754"
                        />
                      </div>
                    </div>
                    <small className="text-muted">
                      {stats.totalSigns > 0
                        ? Math.round(
                            (stats.withSupport / stats.totalSigns) * 100
                          )
                        : 0}
                      % do total
                    </small>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={4} md={6} className="mb-3">
                <Card className="h-100 border-warning">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="text-muted mb-1">Sem Suporte</h6>
                        <h2 className="mb-0 text-warning">
                          {stats.withoutSupport}
                        </h2>
                      </div>
                      <div
                        className="bg-warning bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: '50px', height: '50px' }}
                      >
                        <FeatherIcon
                          icon="alert-triangle"
                          size={24}
                          color="#ffc107"
                        />
                      </div>
                    </div>
                    <small className="text-muted">
                      {stats.totalSigns > 0
                        ? Math.round(
                            (stats.withoutSupport / stats.totalSigns) * 100
                          )
                        : 0}
                      % do total
                    </small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col lg={4} className="mb-3">
                <Card className="h-100">
                  <Card.Header>
                    <h6 className="mb-0">
                      <FeatherIcon
                        icon="briefcase"
                        size={16}
                        className="me-2"
                      />
                      Por Serviço
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    {Object.entries(stats.byService)
                      .filter(([, count]) => count > 0)
                      .map(([service, count]) => (
                        <div
                          key={service}
                          className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom"
                        >
                          <span>
                            <Badge
                              bg={
                                service === 'Implantação'
                                  ? 'success'
                                  : service === 'Remoção'
                                  ? 'danger'
                                  : service === 'Limpeza'
                                  ? 'info'
                                  : 'warning'
                              }
                              className="me-2"
                            >
                              {service}
                            </Badge>
                          </span>
                          <strong>{count}</strong>
                        </div>
                      ))}
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={4} className="mb-3">
                <Card className="h-100">
                  <Card.Header>
                    <h6 className="mb-0">
                      <FeatherIcon icon="tag" size={16} className="me-2" />
                      Por Tipo de Placa
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    {Object.entries(stats.byType)
                      .filter(([, count]) => count > 0)
                      .map(([type, count]) => (
                        <div
                          key={type}
                          className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom"
                        >
                          <span>
                            <Badge
                              bg={
                                type === 'Regulamentação'
                                  ? 'danger'
                                  : type === 'Advertência'
                                  ? 'warning'
                                  : 'primary'
                              }
                              className="me-2"
                            >
                              {type}
                            </Badge>
                          </span>
                          <strong>{count}</strong>
                        </div>
                      ))}
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={4} className="mb-3">
                <Card className="h-100">
                  <Card.Header>
                    <h6 className="mb-0">
                      <FeatherIcon icon="layers" size={16} className="me-2" />
                      Por Película
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    {Object.entries(stats.byFilm)
                      .filter(([, count]) => count > 0)
                      .map(([film, count]) => (
                        <div
                          key={film}
                          className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom"
                        >
                          <span>
                            <Badge bg="secondary" className="me-2">
                              {film}
                            </Badge>
                          </span>
                          <strong>{count}</strong>
                        </div>
                      ))}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col>
                <Card>
                  <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">
                        <FeatherIcon icon="map" size={16} className="me-2" />
                        Mapa Consolidado ({stats.totalSigns} placas)
                      </h6>
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          margin: 0,
                          fontSize: '12px'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={showSignImages}
                          onChange={() => setShowSignImages(!showSignImages)}
                          style={{ marginRight: '6px', cursor: 'pointer' }}
                        />
                        <span>Imagens das placas</span>
                      </label>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <MapComponent
                      lat={mapCenter.lat}
                      lng={mapCenter.lng}
                      zoom={14}
                      markers={mapMarkers}
                      onMarkerClick={handleMarkerClick}
                      height="600px"
                      showZoomControls={true}
                      showSignImages={showSignImages}
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}

      <SignalOverlayViewModal
        show={showOverlayModal}
        onHide={() => {
          setShowOverlayModal(false);
          setSelectedOverlaySign(null);
        }}
        overlaySign={selectedOverlaySign}
      />
    </Container>
  );
};

export default ProjectDashboard;
